import React, { useEffect, useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const toWords = (text) => text.split(',').map((w) => w.trim()).filter(Boolean);
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

/** What a company's ClickUp data means: which folders hold projects, and how to recognise go-live and remedial work. */
export default function ClickupSettings({ connector, connection, companyId, onSaved }) {
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.options(connector.id, companyId)
      .then((res) => {
        if (cancelled) return;
        const o = res.data;
        const s = connection.definitions || {};
        setOptions(o);
        setDraft({
          interval: connection.syncIntervalMinutes,
          workspaceId: s.workspaceId || o.workspaces[0]?.id || '',
          excludedFolders: s.excludedFolders || o.folders.filter((f) => /template|sandbox/i.test(f.name)).map((f) => f.id),
          baselineField: s.baselineField || '',
          goLive: (s.goLiveKeywords || []).join(', '),
          remedial: (s.remedialKeywords || []).join(', '),
          hoursPerDay: s.hoursPerDay,
        });
      })
      .catch((err) => { if (!cancelled) setError(describeError(err, 'Could not read the options from ClickUp')); });
    return () => { cancelled = true; };
    // Options are read once when the settings open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, companyId]);

  if (error && !options) return <div className="error-message" role="alert">{error}</div>;
  if (!options || !draft) return <p className="source-hint">Reading the options from ClickUp…</p>;

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };
  const folders = options.folders.filter((f) => f.workspaceId === draft.workspaceId);

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          workspaceId: draft.workspaceId,
          excludedFolders: draft.excludedFolders,
          baselineField: draft.baselineField || null,
          goLiveKeywords: toWords(draft.goLive),
          remedialKeywords: toWords(draft.remedial),
          hoursPerDay: Number(draft.hoursPerDay),
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>

      {options.workspaces.length > 1 && (
        <label className="connection-field">
          <span>Workspace</span>
          <select value={draft.workspaceId} onChange={(e) => set({ workspaceId: e.target.value })}>
            {options.workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
      )}

      <fieldset className="connection-field">
        <legend>Folders that do not hold projects</legend>
        <p className="source-hint">Tasks and projects in these folders are left out. Time booked to them still counts towards billable time.</p>
        <div className="connection-checks">
          {folders.map((f) => (
            <label key={f.id}>
              <input
                type="checkbox"
                aria-label={`Exclude ${f.name}`}
                checked={draft.excludedFolders.includes(f.id)}
                onChange={() => set({ excludedFolders: draft.excludedFolders.includes(f.id) ? draft.excludedFolders.filter((id) => id !== f.id) : [...draft.excludedFolders, f.id] })}
              />
              {f.name}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="connection-field">
        <span>Field holding the original date</span>
        <select value={draft.baselineField} onChange={(e) => set({ baselineField: e.target.value })}>
          <option value="">Any date field named baseline or original</option>
          {options.dateFields.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </label>

      <label className="connection-field">
        <span>Words that identify a go-live task</span>
        <input type="text" value={draft.goLive} onChange={(e) => set({ goLive: e.target.value })} />
      </label>

      <label className="connection-field">
        <span>Words that identify remedial work</span>
        <input type="text" value={draft.remedial} onChange={(e) => set({ remedial: e.target.value })} />
      </label>
      <p className="source-hint">Separate words with commas. Remedial words are matched against task names and tags; go-live words against task names.</p>

      <label className="connection-field">
        <span>Hours in a working day</span>
        <input type="number" min="0.5" max="24" step="0.5" value={draft.hoursPerDay} onChange={(e) => set({ hoursPerDay: e.target.value })} />
      </label>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. A change to what is read starts a full sync.</span>}
      </div>
    </form>
  );
}
